import type { Logger } from './log.js';
import type { OverlayContext, OverlayRegistry, OverlayWindow } from './overlay-types.js';
import { resolveOverlayUrl } from './script-src.js';

/** True only inside a frame, which is the only place the editor overlay runs. */
export function inIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    // A cross-origin parent throws on access, which itself proves we are framed.
    return true;
  }
}

/**
 * The editor opens a page as `…#siteflow-edit=<token>`. The token travels in the
 * fragment so it is never sent to the customer's server or written to its logs.
 * Only a framed page honours it, and it proves nothing by itself: the CMS must
 * accept the same token before the overlay is allowed to talk to anyone.
 */
export function readEditToken(): string | null {
  if (!inIframe() || typeof location === 'undefined') return null;
  const match = /[#&]siteflow-edit=([A-Za-z0-9._-]{20,4096})(?:&|$)/.exec(location.hash);
  return match?.[1] ?? null;
}

/**
 * Requests the overlay bundle.
 *
 * The overlay is a separate file so its bytes never count against the core
 * budget on the millions of page views that are not being edited.
 */
export function loadOverlay(context: OverlayContext, log: Logger): void {
  const target = window as OverlayWindow;
  const registry: OverlayRegistry = target.__siteflowOverlay ?? { contexts: [] };
  target.__siteflowOverlay = registry;

  if (typeof registry.mount === 'function') {
    registry.mount(context);
    return;
  }

  registry.contexts.push(context);
  if (registry.requested) return;
  registry.requested = true;

  const script = document.createElement('script');
  script.src = resolveOverlayUrl(context.overlayUrl);
  script.async = true;
  script.onerror = () => log.error('Editing overlay failed to load from', script.src);
  document.head.appendChild(script);
}
